import { error } from '@pressreader/logger';

// Minimal slice of `@pressreader/services` `RequestConfig` that this helper needs. Declared locally on
// purpose: importing the type from `services` would close an `appstore -> services -> config -> appstore`
// dependency cycle. Every `RequestConfig` field is optional, so a `{ cancellableKey }`-only object is a
// valid config and any `(arg, config?: RequestConfig)` service is assignable to the service param below.
interface CancellableRequestConfig {
    cancellableKey?: string;
}

/**
 * Wrap an authenticated HTTP service as an RTK Query `queryFn`.
 *
 * Centralizes the boilerplate every endpoint that reuses the existing HTTP layer (rather than an RTKQ
 * `fetchBaseQuery`) would otherwise repeat per endpoint: the success/error envelope, error logging,
 * and — when a `cancelKey` is given — request de-duplication. The abort key is namespaced by the RTKQ
 * endpoint name so the app-wide `cancellableKey` keyspace (`@pressreader/services`) can't collide
 * across features: a second identical in-flight call aborts the previous one (e.g. a double-submitted
 * create/delete can't both reach the server).
 *
 * @example
 * createThing: build.mutation<number, CreateArgs>({
 *     queryFn: queryFnFromService(createThingService, { cancelKey: a => `${a.name}-${a.query}` }),
 * }),
 */
export function queryFnFromService<Arg, Res>(
    service: (arg: Arg, config?: CancellableRequestConfig) => Promise<Res>,
    { cancelKey }: { cancelKey?: (arg: Arg) => string } = {},
) {
    return async (arg: Arg, api?: { endpoint?: string }) => {
        try {
            const config: CancellableRequestConfig | undefined = cancelKey
                ? { cancellableKey: `${api?.endpoint ?? ''}:${cancelKey(arg)}` }
                : undefined;
            return { data: await service(arg, config) };
        } catch (err) {
            error(err);
            return { error: err };
        }
    };
}

/**
 * RTK Query slice reducer path for the shared `baseApi`. Declared here (no RTK import) so cache-reading
 * helpers like `yieldOnRace` and `baseApi` itself agree on the key without coupling those helpers to the
 * RTK-importing `base.api` module. `base.api` imports this so the two can't drift.
 */
export const RTKQ_REDUCER_PATH = '@@rtkq';

// Minimal structural slice of RTK Query's queryFn `api` that `yieldOnRace` needs (kept RTK-free).
interface YieldOnRaceQueryApi {
    getState: () => unknown;
    /** RTKQ passes this for queries; it keys this query's cache entry. */
    queryCacheKey?: string;
}

// The store shape `yieldOnRace` reads: the RTK Query slice's query cache entries, keyed by cache key.
// Generic over `Data` so the one cast below also types the cached `data`, avoiding a second cast.
interface RtkqQueryCacheState<Data> {
    [reducerPath: string]: { queries?: Record<string, { data?: Data } | undefined> } | undefined;
}

/**
 * Wrap a query's `queryFn` so it YIELDS to an optimistic cache patch that landed while it was in flight,
 * instead of overwriting it.
 *
 * The race: a `getX` fetch is already running when a mutation optimistically patches `getX`'s cache (via
 * `updateQueryData`). When the fetch resolves, RTK Query writes its now-stale (pre-mutation) result over
 * the patch — a visible flicker the mutation's `invalidatesTags` refetch then has to undo.
 * `invalidationBehavior: 'delayed'` defers NEW refetches during a mutation but can't stop a fetch that was
 * ALREADY running when the mutation started.
 *
 * The guard: a cache entry's `data` reference changes iff something patched it (immer yields a new
 * reference; a plain refetch retains the previous reference while pending). So snapshot `data` before the
 * fetch and re-read after; if it changed (and there was data to begin with), an optimistic patch raced us
 * — return the current cache as-is rather than clobber it. The mutation's `invalidatesTags` refetch then
 * reconciles to server truth. The query stays a normal `pending -> fulfilled` (no abort, no error state),
 * so consumers never observe the race. Same shape as the identity fence's own settle-time re-check
 * (`identityScoped`), applied to a different hazard: that one compares the identity generation, this one the
 * cache entry's `data` reference.
 *
 * REQUIRES the patching mutation to `invalidatesTags` this query. yieldOnRace discards the in-flight
 * fetch's server data to preserve the patch, so the mutation's own refetch is the ONLY thing that then
 * reconciles the optimistic value to truth — a mutation that patches a guarded query but opts out of
 * invalidating its tag would leave the yield unconfirmed (and a failed such mutation would never revert).
 *
 * Generic over the `api` type so it composes under other queryFn combinators that ENRICH that object —
 * notably `identityScoped(yieldOnRace(fn))`, where the inner queryFn annotates its api as
 * `IdentityQueryApi` to read `api.identity`. Keep `identityScoped` outermost (see its docs).
 *
 * @example
 * getItems: build.query<Item[], void>({
 *     queryFn: yieldOnRace(async () => ({ data: (await fetchItems()).map(mapToItem) })),
 *     providesTags: [TAG_ITEMS],
 * }),
 */
export function yieldOnRace<Arg, Api extends YieldOnRaceQueryApi, Result extends { data: unknown } | { error: unknown }>(
    queryFn: (arg: Arg, api: Api) => Promise<Result>,
): (arg: Arg, api: Api) => Promise<Result> {
    const wrapped = async (arg: Arg, api: Api): Promise<Result | { data: unknown }> => {
        const readCachedData = (): unknown => {
            // RTKQ types `getState()` as `unknown`; assert the shape of the slice we read.
            const root = api.getState() as RtkqQueryCacheState<unknown>;
            return root[RTKQ_REDUCER_PATH]?.queries?.[api.queryCacheKey ?? '']?.data;
        };
        const before = readCachedData();
        const result = await queryFn(arg, api);
        // Only a successful fetch can clobber. A changed `data` reference means an optimistic patch landed
        // while we were fetching — yield to it rather than overwrite (invalidatesTags reconciles).
        if ('data' in result) {
            const after = readCachedData();
            if (before !== undefined && after !== undefined && after !== before) {
                return { data: after };
            }
        }
        return result;
    };
    // Re-expose the wrapped queryFn's exact signature so the query's data type flows through unchanged —
    // the yield branch returns the same-shaped cached `data` (this very entry's).
    return wrapped as (arg: Arg, api: Api) => Promise<Result>;
}
